import { createContext, useCallback, useContext } from 'react';
import { PageFeed, PageFeedComment, User } from 'model/indexTS';
import { useTranslation } from 'utils/translation';
import { PageState, usePageSelectors } from 'services/page';
import { usePageFeedCommentActions, usePageFeedCommentSelectors } from 'services/page-feed-comments';
import { usePermissionsSelectors } from 'services/permissions';
import { useUserSelectors } from 'services/user';
import { useReactAlert } from 'components/react-alert-provider';
import { useAuthSelectors } from 'services/auth';

export interface UseFeedCommentsOutput {
  page: PageState;
  feed: PageFeed;
  isStaff: boolean;
  user: User | null;
  isCreating?: boolean;
  isDeleting?: boolean;
  isUserAuthenticated: boolean;
  handleDeleteComment: (feedCommentId: string) => Promise<boolean>;
  handleAddComment: (content: string) => Promise<boolean>;
  handleLikeComment: (feedComment: PageFeedComment) => Promise<void>;
}

export interface FeedCommentContextState {
  feed: PageFeed;
}
export const FeedCommentContext = createContext<FeedCommentContextState | null>(null);

export const useFeedComments = (): UseFeedCommentsOutput => {
  const { page } = usePageSelectors();
  const { feed } = useContext(FeedCommentContext) as FeedCommentContextState;
  const { user } = useUserSelectors();
  const { deletePageFeedComment, createPageFeedComment, likePageFeedComment } = usePageFeedCommentActions();
  const { t } = useTranslation('pageFeed');
  const { showSuccess, showError } = useReactAlert();
  const { isCreating, isDeleting } = usePageFeedCommentSelectors(feed._id);
  const { isStaff } = usePermissionsSelectors();
  const { isUserAuthenticated } = useAuthSelectors();

  const handleDeleteComment = useCallback(
    async (feedCommentId: string) => {
      const response = await deletePageFeedComment({ feedCommentId, feedId: feed._id, slug: page.slug });
      if (response.error) {
        showError(t('pageFeed:feedDetail.comments.delete.deleteError'));
        return false;
      }

      showSuccess(t('pageFeed:feedDetail.comments.delete.deleteSuccess'));
      return true;
    },
    [feed._id, deletePageFeedComment, page.slug, showError, showSuccess, t],
  );

  const handleAddComment = useCallback(
    async (content: string) => {
      const response = await createPageFeedComment(page.slug, feed._id, { content });
      if (response.error) {
        showError(t('pageFeed:feedDetail.comments.error'));
        return false;
      }

      return true;
    },
    [feed._id, createPageFeedComment, page.slug, showError, t],
  );

  const handleLikeComment = useCallback(
    async (feedComment: PageFeedComment) => {
      const response = await likePageFeedComment({
        feedCommentId: feedComment._id,
        feedId: feedComment.feedId,
        liked: !feedComment.summary.liked,
        slug: page.slug,
      });
      if (response.error) {
        showError(t('pageFeed:feedDetail.summary.error'));
      }
    },
    [likePageFeedComment, page.slug, showError, t],
  );

  return {
    handleDeleteComment,
    handleAddComment,
    handleLikeComment,
    isCreating,
    isDeleting,
    feed,
    page,
    isStaff,
    user,
    isUserAuthenticated,
  };
};
